import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Activity, AlertTriangle, Award, Building2, Copy, MessageCircle,
  RefreshCcw, Send, Sparkles, Target, Users, X,
  CheckCircle2, Megaphone, ClipboardList,
} from "lucide-react";
import { api } from "../lib/api";
import AssignTaskModal from "../components/AssignTaskModal";

import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import MetricDisplay from "../components/MetricDisplay";
import EditableValue from "../components/EditableValue";
import { getCurrentCustomer } from "../lib/customerSession";

// ---------------------------------------------------------------------------
// Theme + helpers
// ---------------------------------------------------------------------------
const STATUS = {
  green: { bar: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700", left: "border-l-4 border-emerald-500", soft: "bg-emerald-50/70" },
  amber: { bar: "bg-amber-500",   text: "text-amber-700",   chip: "bg-amber-100 text-amber-700",     left: "border-l-4 border-amber-500",   soft: "bg-amber-50/70"   },
  red:   { bar: "bg-red-500",     text: "text-red-700",     chip: "bg-red-100 text-red-700",         left: "border-l-4 border-red-500",     soft: "bg-red-50/70"     },
  blue:  { bar: "bg-blue-500",    text: "text-blue-700",    chip: "bg-blue-100 text-blue-700",       left: "border-l-4 border-blue-500",    soft: "bg-blue-50/70"    },
};

const CMO_DASH = "—";

function fmtCmoINR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const num = Number(n);
  const v = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (v >= 1_00_00_000) return `${sign}₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000)    return `${sign}₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1000)        return `${sign}₹${(v / 1000).toFixed(1)}K`;
  return `${sign}₹${Math.round(v).toLocaleString("en-IN")}`;
}

function Section({ title, subtitle, right, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-500">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Modal({ open, onClose, title, children, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="rounded p-1 text-slate-400 hover:text-[#0F1B2D]"><X size={16} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — Today's Marketing Tasks (CMO Universal Daily Marketing
// Operating System, Phase 1, 2026-08). Deterministic, rule-based — NO LLM.
// Same canonical rule_id (cmo_task_<key>) Command Center reads; completing
// or dismissing here hits the exact same /command-center/act endpoint, so
// both surfaces always agree on task state.
// ---------------------------------------------------------------------------
const TASK_PRIORITY_STYLE = {
  1: { label: "HIGH",   chip: "bg-[#B44554] text-white" },
  3: { label: "MEDIUM", chip: "bg-amber-100 text-amber-700" },
  5: { label: "LOW",    chip: "bg-slate-100 text-slate-600" },
};

function MarketingTaskCard({ task, onAct, onAssign }) {
  const navigate = useNavigate();
  const pr = TASK_PRIORITY_STYLE[task.priority] || TASK_PRIORITY_STYLE[3];
  return (
    <div data-testid={`cmo-daily-task-${task.rule_id}`}
      className="rounded-lg border border-[#FFCED1] bg-white p-4 shadow-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wide ${pr.chip}`}>{pr.label}</span>
        {task.channel && <span className="text-[11px] text-slate-500">{task.channel}</span>}
        <span className="ml-auto text-[11px] font-medium text-slate-400">
          {task.timing_window === "today" ? "Today" : task.timing_window === "this_week" ? "This Week" : "Upcoming"}
        </span>
      </div>
      <h4 className="mt-2 text-[13.5px] font-bold text-[#0F1B2D]" data-testid={`cmo-daily-task-${task.rule_id}-title`}>{task.title}</h4>
      <p className="mt-1 text-[12px] leading-snug text-slate-600"><span className="font-semibold text-slate-500">Why: </span>{task.rationale}</p>
      {task.target_audience && (
        <p className="mt-1 text-[11.5px] text-slate-500"><span className="font-semibold">Audience: </span>{task.target_audience}</p>
      )}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button data-testid={`cmo-daily-task-${task.rule_id}-complete`}
          onClick={() => onAct(task, "complete")}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-emerald-700">
          <CheckCircle2 size={12} /> Mark done
        </button>
        <button data-testid={`cmo-daily-task-${task.rule_id}-dismiss`}
          onClick={() => onAct(task, "dismiss")}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50">
          Dismiss
        </button>
        <button data-testid={`cmo-daily-task-${task.rule_id}-playbook`}
          onClick={() => navigate(`/cmo/playbook?rule_id=${encodeURIComponent(task.rule_id)}`)}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-md border border-[#B44554]/30 bg-[#FFF5F6] px-3 py-2 text-[12px] font-semibold text-[#B44554] hover:bg-[#FFE7E9]">
          <ClipboardList size={12} /> Campaign Playbook
        </button>
        <button data-testid={`cmo-daily-task-${task.rule_id}-assign`}
          onClick={() => onAssign({
            rule_id: task.rule_id, title: task.title, priority: task.priority,
            detail: task.rationale,
            due_timeline: task.timing_window === "today" ? "Today" : task.timing_window === "this_week" ? "This Week" : "Upcoming",
          })}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-100">
          <MessageCircle size={12} /> Assign Task
        </button>
      </div>
    </div>
  );
}

function daysRemainingFrom(rationale) {
  const m = /(-?\d+)\s*day/.exec(rationale || "");
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

function OpportunityRow({ task, accent = "#B44554" }) {
  const navigate = useNavigate();
  const days = daysRemainingFrom(task.rationale);
  return (
    <button data-testid={`cmo-opportunity-${task.rule_id}`}
      onClick={() => navigate(`/cmo/playbook?rule_id=${encodeURIComponent(task.rule_id)}`)}
      className="flex w-full items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-[#FFCED1] hover:bg-[#FFF9FA]">
      <div className="min-w-0">
        <p className="truncate text-[12.5px] font-semibold text-[#0F1B2D]">{task.title}</p>
        {task.target_audience && <p className="truncate text-[11px] text-slate-500">{task.target_audience}</p>}
      </div>
      <span className="shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold" style={{ color: accent, backgroundColor: `${accent}1A` }}>
        {days != null ? (days <= 0 ? "Today" : `${days}d`) : (task.timing_window === "today" ? "Today" : "Upcoming")}
      </span>
    </button>
  );
}

function CampaignPlaybooksSummary() {
  const [playbooks, setPlaybooks] = useState(null); // null = loading
  useEffect(() => {
    (async () => {
      const res = await api.cmoPlaybookList(5);
      setPlaybooks(res.ok && Array.isArray(res.data?.playbooks) ? res.data.playbooks : []);
    })();
  }, []);

  return (
    <Section testid="cmo-playbooks-summary" title="Campaign Playbooks" subtitle="Active / recently touched execution plans">
      {playbooks === null ? (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">Loading…</div>
      ) : playbooks.length === 0 ? (
        <div data-testid="cmo-playbooks-summary-empty" className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
          No campaign playbooks yet — open any task above and tap &quot;Campaign Playbook&quot; to build one.
        </div>
      ) : (
        <div className="space-y-2">
          {playbooks.map((p) => (
            <Link key={p.id} to={`/cmo/playbook?rule_id=${encodeURIComponent(p.rule_id)}`}
              data-testid={`cmo-playbook-summary-${p.id}`}
              className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2.5 hover:border-[#FFCED1] hover:bg-[#FFF9FA]">
              <div className="min-w-0">
                <p className="truncate text-[12.5px] font-semibold text-[#0F1B2D]">{p.context?.task_title || (p.playbook_type || "").replace(/_/g, " ")}</p>
                <p className="text-[11px] text-slate-500">{(p.playbook_type || "").replace(/_/g, " ")}</p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold text-slate-600">{p.status}</span>
            </Link>
          ))}
        </div>
      )}
    </Section>
  );
}

function TodaysMarketingTasks() {
  const [allTasks, setAllTasks] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshError, setRefreshError] = useState(false);
  const [assignItem, setAssignItem] = useState(null);
  const loadedOnce = useRef(false);

  const load = async () => {
    if (!loadedOnce.current) setLoading(true);
    const res = await api.cmoDailyTasks();
    setLoading(false);
    if (res.ok && Array.isArray(res.data?.today)) {
      loadedOnce.current = true;
      setRefreshError(false);
      setTodayTasks(res.data.today);
      setAllTasks(Array.isArray(res.data.tasks) ? res.data.tasks : res.data.today);
    } else if (loadedOnce.current) {
      // Keep the last good data visible — never blank the screen on a
      // transient refresh failure (Phase 6, section 12/13).
      setRefreshError(true);
    } else {
      setTodayTasks([]);
      setAllTasks([]);
    }
  };

  useEffect(() => { load(); }, []);

  const onAct = async (task, action) => {
    // Optimistic removal — the /command-center/act rebuild can take several
    // seconds (full decision/execution-plan recompute); don't block the
    // button on it. Roll back + toast if the call actually fails.
    setTodayTasks((t) => t.filter((x) => x.rule_id !== task.rule_id));
    setAllTasks((t) => t.filter((x) => x.rule_id !== task.rule_id));
    const res = await api.commandCenterActChain({
      action, ruleId: task.rule_id, decisionId: task.id, title: task.title, role: "cmo",
    });
    if (!res.ok) {
      setTodayTasks((t) => (t.some((x) => x.rule_id === task.rule_id) ? t : [...t, task]));
      setAllTasks((t) => (t.some((x) => x.rule_id === task.rule_id) ? t : [...t, task]));
    }
  };

  const todayIds = new Set(todayTasks.map((t) => t.rule_id));
  const upcoming = allTasks.filter((t) => t.category === "event" && !todayIds.has(t.rule_id)).slice(0, 6);
  const growth = allTasks.filter((t) => ["retention", "growth", "content"].includes(t.category) && !todayIds.has(t.rule_id)).slice(0, 6);

  return (
    <div className="space-y-4">
      <Section
        testid="cmo-daily-tasks"
        title="Today's Marketing Tasks"
        subtitle="Deterministic, rule-based — product, segment, country & calendar aware. No AI guesswork."
        right={<Megaphone size={16} className="text-[#B44554]" />}
      >
        {refreshError && (
          <div data-testid="cmo-daily-tasks-refresh-error" className="mb-3 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
            <span>Unable to refresh marketing intelligence. Showing the last available data.</span>
            <button data-testid="cmo-daily-tasks-retry" onClick={load} className="shrink-0 font-semibold underline">Retry</button>
          </div>
        )}
        {loading ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
            Loading today's marketing priorities…
          </div>
        ) : todayTasks.length === 0 ? (
          <div data-testid="cmo-daily-tasks-empty" className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
            <span className="block text-[13px] font-semibold text-slate-600">You&apos;re clear for today.</span>
            <span className="mt-1 block">Check back as your Product, Segment and Country change, or as new opportunities appear.</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-3">
            {todayTasks.map((t) => (
              <MarketingTaskCard key={t.rule_id} task={t} onAct={onAct} onAssign={setAssignItem} />
            ))}
          </div>
        )}
      </Section>

      {!loading && (
        <Section testid="cmo-upcoming-opportunities" title="Upcoming Opportunities" subtitle="Identified by the marketing calendar engine — not shown until relevant">
          {upcoming.length === 0 ? (
            <div data-testid="cmo-upcoming-opportunities-empty" className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
              No major marketing opportunity is approaching for your selected markets.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 tablet:grid-cols-2 desktop:grid-cols-3">
              {upcoming.map((t) => <OpportunityRow key={t.rule_id} task={t} />)}
            </div>
          )}
        </Section>
      )}

      {!loading && (
        <Section testid="cmo-growth-opportunities" title="Customer Growth Opportunities" subtitle="Retention, cross-sell, testimonials & UGC">
          {growth.length === 0 ? (
            <div data-testid="cmo-growth-opportunities-empty" className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
              Connect customer data to unlock retention and cross-sell opportunities.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 tablet:grid-cols-2 desktop:grid-cols-3">
              {growth.map((t) => <OpportunityRow key={t.rule_id} task={t} accent="#0F766E" />)}
            </div>
          )}
        </Section>
      )}

      {!loading && <CampaignPlaybooksSummary />}
      <AssignTaskModal item={assignItem} onClose={() => setAssignItem(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — AI Strategic Insights (real-only, no demo fallback)
// ---------------------------------------------------------------------------
function InsightCard({ insight }) {
  const s = STATUS[insight.severity] || STATUS.blue;
  return (
    <div data-testid={`cmo-insight-${insight.id}`} className={`rounded-lg ${s.left} ${s.soft} p-4`}>
      <h4 className="text-[13.5px] font-bold text-[#0F1B2D]">{insight.title}</h4>
      <p className="mt-1 text-[12.5px] leading-snug text-slate-700">{insight.body}</p>
      <p className="mt-2 text-[12px] font-semibold text-emerald-700">{insight.action}</p>
    </div>
  );
}

function InsightsGrid({ dataAvailable = false }) {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const pollTimerRef = useRef(null);

  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  const refresh = async (force = false, silent = false) => {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    if (!silent) { setLoading(true); setRefreshError(false); }
    const res = await api.aiInsightsGenerate("cmo", api.cmoInsights, force);
    if (!silent) setLoading(false);
    if (res.ok) {
      const newItems = Array.isArray(res.data?.items) ? res.data.items : [];
      const bg = !!res.data?.generating;
      if (newItems.length >= 3) { setItems(newItems); setSource(res.data?.source === "ai" ? "ai" : "empty"); }
      else if (!bg) { setItems([]); setSource("empty"); }
      setGenerating(bg);
      if (bg) pollTimerRef.current = setTimeout(() => refresh(false, true), 15000);
    } else {
      setGenerating(false);
      setItems((prev) => { if (prev.length > 0) { setRefreshError(true); return prev; } return []; });
      setSource((prev) => (prev === "ai") ? "ai" : "empty");
    }
  };

  useEffect(() => {
    refresh(false);
  }, [dataAvailable]);

  return (
    <section data-testid="cmo-insights" className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Executive Insights</div>
          <div className="text-[13px] text-slate-500">
            What your CMO would tell you today
            {source === "ai" && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700">C-ORE generated</span>}
            {generating && <span data-testid="cmo-insights-generating-badge" className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10.5px] font-medium text-blue-700">Updating…</span>}
          </div>
        </div>
        <button data-testid="cmo-insights-refresh" onClick={() => refresh(true)} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11.5px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50">
          <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading…" : "Refresh insights"}
        </button>
      </div>
      {refreshError && (
        <div data-testid="cmo-insights-refresh-error" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-700">
          Refresh failed — showing previous insights. Try again shortly.
        </div>
      )}
      {generating && items.length > 0 && (
        <div data-testid="cmo-insights-updating" className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11.5px] text-blue-700">
          Updating insights in the background — new cards will appear automatically.
        </div>
      )}
      {items.length === 0 ? (
        <div data-testid="cmo-insights-empty" className="rounded-xl border border-dashed border-blue-200 bg-blue-50/50 px-4 py-8 text-center text-[12.5px] text-blue-700">
          {loading ? "Loading insights…" : generating ? "Insights are being prepared — they'll appear here shortly." : "Connect Tally and run a sync to unlock personalised strategic insights."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-2">
          {items.map((it) => <InsightCard key={it.id} insight={it} />)}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SECTION — NPS + referral pings (real debtor list + real owner name)
// ---------------------------------------------------------------------------
function NPSAndReferrals({ customerData }) {
  const [refOpen, setRefOpen] = useState(false);
  const [refTarget, setRefTarget] = useState(null);
  const [copied, setCopied] = useState(false);

  const ownerName    = customerData?.owner_name || customerData?.contact_name || null;
  const realCompany  = customerData?.company_name || null;
  const signOffName  = ownerName || "—";
  const signOffCo    = realCompany || "your team";

  const askReferral = (p) => { setRefTarget(p); setRefOpen(true); setCopied(false); };
  const message = (name) => (
    `Dear ${name}, thank you for your continued trust in ${signOffCo}. We are growing and would love to serve more businesses like yours. Could you introduce us to 2 or 3 businesses in your network who might benefit from our work? We would be grateful.\n— ${signOffName}, ${signOffCo}`
  );
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(message(refTarget?.name || "Customer")); setCopied(true); }
    catch (_e) { setCopied(false); }
  };
  const onWhatsApp = () => {
    const text = encodeURIComponent(message(refTarget?.name || "Customer"));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const npsScore = customerData?.nps_score ?? null;
  const debtors  = customerData?.debtors_list || [];
  const referralTargets = debtors
    .slice()
    .sort((a, b) => (Number(b.outstanding_amount) || 0) - (Number(a.outstanding_amount) || 0))
    .slice(0, 3)
    .map((d, i) => ({ id: `cust-${i}`, name: d.customer_name || `Customer ${i + 1}` }));

  return (
    <Section
      testid="cmo-nps"
      title={`Net Promoter Score${npsScore != null ? ` — ${npsScore >= 0 ? "+" : ""}${npsScore}` : ""}`}
      subtitle={npsScore != null
        ? "Captured from your last customer survey (set in admin → Company Parameters)"
        : "Enter NPS in admin → Company Parameters, or run a survey via WhatsApp"}
    >
      {npsScore == null ? (
        <div className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12px] text-slate-500">
          <span className="text-slate-400 text-[18px] leading-none">—</span>
          <span>NPS not yet captured. Enter your last survey score in admin → Company Parameters to unlock this section, or trigger a one-tap WhatsApp survey from the Smart Alerts module.</span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 text-[12px] text-emerald-800">
            <b>NPS {npsScore >= 0 ? "+" : ""}{npsScore}</b> ·
            {npsScore >= 50
              ? " strong loyalty — focus on activating referrals from your top accounts."
              : npsScore >= 30
                ? " healthy but not viral — work on closing service gaps to push promoters up."
                : " below industry median — investigate detractor feedback before scaling marketing."}
          </div>
        </div>
      )}

      {referralTargets.length > 0 && (
        <>
          <div className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Ask top customers for referrals
          </div>
          <div className="mt-2 grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-3">
            {referralTargets.map((p) => (
              <div key={p.id} data-testid={`nps-promoter-${p.id}`} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                <div className="text-[12.5px] font-bold text-[#0F1B2D]">{p.name}</div>
                <div className="text-[11px] text-slate-500">From your live Tally debtor list</div>
                <button data-testid={`nps-ask-${p.id}`} onClick={() => askReferral(p)}
                  className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-emerald-700">
                  <MessageCircle size={11} /> Ask for Referral
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal open={refOpen} onClose={() => setRefOpen(false)} title={refTarget ? `Ask ${refTarget.name} for a referral` : "Ask for referral"} testid="cmo-referral-modal">
        {refTarget && (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-3 text-[12.5px] leading-snug text-slate-700 whitespace-pre-wrap">{message(refTarget.name)}</div>
            <div className="flex items-center gap-2">
              <button data-testid="referral-whatsapp" onClick={onWhatsApp} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700">
                <MessageCircle size={12} /> Send via WhatsApp
              </button>
              <button data-testid="referral-copy" onClick={onCopy} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-[12.5px] font-semibold text-[#1E3A5F] hover:bg-slate-50">
                <Copy size={12} /> {copied ? "Copied!" : "Copy Message"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// CMO Chat
// ---------------------------------------------------------------------------
function CMOTyping() {
  return (
    <div data-testid="cmo-chat-typing" className="max-w-[85%] rounded-2xl bg-[#EFF6FF] px-4 py-3">
      <div className="flex items-center gap-1">
        {[0,200,400].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: `${d}ms` }} />)}
      </div>
    </div>
  );
}

function CMOChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sid = useRef(`core-cmo-${Math.random().toString(36).slice(2, 10)}`);
  const scroll = useRef(null);

  useEffect(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [messages, sending]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    const res = await api.cmoChat(t, sid.current);
    const reply = res.ok
      ? (res.data?.reply || "Sorry, I could not process that right now. Please try again in a moment.")
      : (res.error || "Sorry, I could not process that right now. Please try again in a moment.");
    setMessages((m) => [...m, { role: "ai", text: reply }]);
    setSending(false);
  };

  return (
    <section data-testid="cmo-chat" className="rounded-xl border border-slate-200 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">Ask your CMO</div>
          <div className="text-[12px] text-slate-500">Customers, pipeline, growth · Hindi or English</div>
        </div>
        <Sparkles size={16} className="text-emerald-600" />
      </div>
      <div ref={scroll} className="space-y-2 overflow-y-auto px-5 py-3" style={{ maxHeight: 280 }}>
        {messages.length === 0 && !sending && (
          <div data-testid="cmo-chat-empty" className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            Ask anything about customers, pipeline, or growth. Try: &quot;Which customer is most at risk?&quot; or &quot;How do I reduce my CAC?&quot;
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`cmo-chat-msg-${i}`}
            className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${m.role === "ai" ? "bg-[#EFF6FF] text-[#0F1B2D]" : "ml-auto bg-emerald-600 text-white"}`}>
            {m.text}
          </div>
        ))}
        {sending && <CMOTyping />}
      </div>
      <div className="border-t border-slate-100 px-5 py-3">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <input data-testid="cmo-chat-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Thinking…" : "Ask about customers, pipeline, or growth strategy"}
            disabled={sending}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-[#0F1B2D] placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
          />
          <button type="submit" data-testid="cmo-chat-send" disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={13} /> Send
          </button>
        </form>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Active-customer module body (real data only — dash when missing)
// ---------------------------------------------------------------------------
function RealCmoKpi({ id, label, icon: Icon, value, format = "currency", status = "blue", sub, tooltip, editable = null }) {
  const s = STATUS[status] || STATUS.blue;
  return (
    <div data-testid={`cmo-kpi-${id}`}
      data-paramkey={editable?.paramKey || undefined}
      className="relative overflow-hidden rounded-lg border border-[#DBEAFE] bg-white p-4 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 ${s.bar}`} />
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">{Icon && <Icon size={13} className={s.text} />}{label}</span>
      </div>
      <div className="mt-2">
        {editable && editable.customerId ? (
          <span className="text-[20px] font-bold tabular-nums text-[#0F1B2D]">
            <EditableValue
              testId={`cmo-kpi-${id}-edit`}
              displayValue={editable.formatted ?? (value != null ? value : CMO_DASH)}
              rawValue={editable.rawValue}
              paramKey={editable.paramKey}
              paramLabel={label}
              customerId={editable.customerId}
              transformIn={editable.transformIn}
              transformOut={editable.transformOut}
              lastUpdatedIso={editable.lastUpdatedIso}
              onSaved={editable.onSaved}
              inputPlaceholder={editable.placeholder}
            />
          </span>
        ) : (
          <MetricDisplay value={value} format={format} label={label} tooltipText={tooltip} size="large" />
        )}
      </div>
      {sub && <div className="mt-1 text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — Customer Portfolio (real debtor list, sorted by outstanding)
// Adjustment 1: real columns (name / outstanding / days) — CRM-dependent
// columns like NPS / last-order / contact are not shown here; they appear
// in dedicated CRM tiles below with their own connector-empty-state.
// ---------------------------------------------------------------------------
function RealCustomerPortfolio({ debtors, debtorCount, totalOutstanding }) {
  const list = Array.isArray(debtors) ? debtors : [];
  const sorted = list
    .filter((d) => d && d.outstanding_amount && Number(d.outstanding_amount) > 0)
    .slice()
    .sort((a, b) => (Number(b.outstanding_amount) || 0) - (Number(a.outstanding_amount) || 0));

  return (
    <Section
      testid="cmo-portfolio"
      title={`Customer Portfolio${debtorCount ? ` — ${debtorCount} Active` : ""}`}
      subtitle={totalOutstanding ? `Total outstanding ${fmtCmoINR(totalOutstanding) || CMO_DASH}` : "Sorted by outstanding amount · from your Tally debtors"}
    >
      {sorted.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
          No customers found in Tally debtors.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3 text-right">Outstanding</th>
                <th className="py-2 pr-3 text-right">Days</th>
                <th className="py-2 pr-3">Risk</th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 12).map((c, i) => {
                const days = Number(c.days_outstanding || 0);
                const risk = days > 60 ? "red" : days > 30 ? "amber" : "green";
                const s = STATUS[risk];
                return (
                  <tr key={`real-debtor-${i}`} data-testid={`cmo-cust-real-${i}`} className="border-t border-slate-100">
                    <td className="py-2 pr-3 font-medium text-[#0F1B2D]">{c.customer_name || CMO_DASH}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#0F1B2D]">{fmtCmoINR(c.outstanding_amount) || CMO_DASH}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{days}d</td>
                    <td className="py-2 pr-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${s.chip}`}>{risk === "red" ? "At Risk" : risk === "amber" ? "Monitor" : "Stable"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function StatTile({ label, value, format, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-slate-500 text-[11px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="mt-1">
        {value === null || value === undefined
          ? <MetricDisplay value={null} format={format || "raw"} label={label} size="medium" />
          : <div className="text-[16px] font-bold text-[#0F1B2D]">{value}</div>}
      </div>
    </div>
  );
}

function PlaceholderTile({ label, tooltip, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-slate-500 text-[11px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="mt-1">
        {value
          ? <div className="text-[16px] font-bold text-[#0F1B2D]">{value}</div>
          : <MetricDisplay value={null} format="raw" label={label} tooltipText={tooltip} size="medium" />}
      </div>
    </div>
  );
}

function RealCmoBody({ customerData, dataAvailable, companyName, reload }) {
  const debtors    = customerData?.debtors_raw || customerData?.debtors_array || customerData?.debtors?.debtors_raw
                  || (Array.isArray(customerData?.debtors) ? customerData.debtors : []);
  const debtorBlk  = (typeof customerData?.debtors === "object" && !Array.isArray(customerData?.debtors)) ? customerData.debtors : {};
  // Adjustment 2: derive "Active Accounts" count directly from the real
  // debtor list when the engine count is missing, so the header never
  // falls back to a hardcoded number.
  const debtorList = Array.isArray(debtorBlk?.debtors_raw) ? debtorBlk.debtors_raw
                    : Array.isArray(debtors) ? debtors : [];
  const debtorCount      = debtorBlk?.debtor_count ?? (debtorList.length || null);
  const totalOutstanding = debtorBlk?.total_debtors ?? null;
  const efficiency = customerData?.efficiency || {};
  const cmo        = customerData?.cmo || {};
  const params     = customerData?.params || {};

  const cac           = cmo.cac;
  const npsScore      = params.nps_score ?? null;
  const marketing     = params.marketing_spend ?? null;
  const newCustomers  = params.new_customers_this_month ?? null;
  const churnRisk     = cmo.churn_risk_count;
  const collEff       = efficiency.collection_efficiency;
  const dso           = efficiency.dso_days;

  // ---- Zoho CRM pipeline metrics for the top KPI tiles ----
  const zCRMTop          = customerData?.zoho?.crm || {};
  const zohoConnectedTop = !!(zCRMTop.deal_count || zCRMTop.contact_count);
  const pipelineValue    = zohoConnectedTop ? (zCRMTop.deal_total_amount ?? null) : null;
  const pipelineStages   = zCRMTop.pipeline_by_stage || zCRMTop.pipeline_stage_counts || {};
  const winningStageCount = Object.entries(pipelineStages).reduce((acc, [stage, n]) => {
    const s = (stage || "").toLowerCase();
    return acc + (s.includes("won") || s.includes("closed won") || s === "closedwon" ? Number(n || 0) : 0);
  }, 0);
  const conversionRate   = zohoConnectedTop && zCRMTop.deal_count
    ? Number(((winningStageCount / zCRMTop.deal_count) * 100).toFixed(1))
    : null;

  // Inline edit plumbing
  const customerId = customerData?.customer_id || (() => { try { return getCurrentCustomer()?.id || null; } catch (_e) { return null; } })();
  const updatedAt  = params.params_updated_at || {};
  const handleSaved = () => { if (typeof reload === "function") reload(); };

  return (
    <div className="space-y-5">
      {/* Section 0 — Today's Marketing Tasks (CMO Daily Marketing Operating System) */}
      <TodaysMarketingTasks />

      {/* Section 1 — Marketing Performance (supporting analytics — de-emphasized;
          Today's actions above must dominate, per Phase 6). */}
      <div className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Marketing Performance</div>
      <section data-testid="cmo-kpis" className="grid grid-cols-2 gap-3 desktop:grid-cols-6">
        <RealCmoKpi id="cac" label="Customer Acquisition Cost" icon={Target}
          value={cac} format="currency" status={cac !== null && cac > 20000 ? "red" : "green"}
          sub={cac !== null
            ? `${fmtCmoINR(marketing) || CMO_DASH} / ${newCustomers ?? CMO_DASH} new`
            : (marketing ? `${fmtCmoINR(marketing)} / mo — add new-customer count to compute CAC` : "Tap pencil to set monthly marketing spend")}
          tooltip="Monthly marketing spend. Powers CAC = marketing ÷ new customers."
          editable={{
            customerId,
            paramKey: "marketing_spend",
            rawValue: marketing,
            transformIn:  (v) => v ?? "",
            transformOut: (v) => v === "" ? null : Math.round(Number(v)),
            lastUpdatedIso: updatedAt.marketing_spend,
            onSaved: handleSaved,
            placeholder: "50000",
            formatted: cac !== null ? fmtCmoINR(cac) : (marketing ? fmtCmoINR(marketing) : CMO_DASH),
          }} />
        <RealCmoKpi id="pipeline" label="Pipeline Value" icon={Building2}
          value={pipelineValue} format="currency"
          status={pipelineValue !== null ? "green" : "blue"}
          sub={pipelineValue !== null
            ? `across ${zCRMTop.deal_count} deal${zCRMTop.deal_count === 1 ? "" : "s"} from Zoho CRM`
            : null}
          tooltip={zohoConnectedTop
            ? `Sum of Amount across all ${zCRMTop.deal_count} deals in Zoho CRM.`
            : "Connect Zoho CRM to see pipeline data."} />
        <RealCmoKpi id="conversion" label="Lead Conversion Rate" icon={Activity}
          value={conversionRate} format="percentage"
          status={conversionRate !== null && conversionRate >= 10 ? "green" : conversionRate !== null ? "amber" : "blue"}
          sub={conversionRate !== null
            ? `${winningStageCount} won / ${zCRMTop.deal_count} total`
            : null}
          tooltip={zohoConnectedTop
            ? `Won deals (${winningStageCount}) ÷ total deals (${zCRMTop.deal_count}). Counts any Zoho stage containing "Won" or "Closed Won".`
            : "Requires CRM integration."} />
        <RealCmoKpi id="nps" label="NPS Score" icon={Award}
          value={npsScore} format="number" status={npsScore !== null && npsScore >= 50 ? "green" : "amber"}
          sub={npsScore !== null ? "from your last survey" : "Tap pencil to set"}
          tooltip="Net Promoter Score from your last customer survey."
          editable={{
            customerId,
            paramKey: "nps_score",
            rawValue: npsScore,
            transformIn:  (v) => v ?? "",
            transformOut: (v) => v === "" ? null : Math.round(Number(v)),
            lastUpdatedIso: updatedAt.nps_score,
            onSaved: handleSaved,
            placeholder: "62",
            formatted: npsScore !== null ? String(npsScore) : CMO_DASH,
          }} />
        <RealCmoKpi id="churn_risk" label="Churn Risk Accounts" icon={AlertTriangle}
          value={churnRisk} format="number" status={churnRisk !== null && churnRisk > 0 ? "red" : "green"}
          sub={churnRisk !== null ? "debtors > 60 days" : null}
          tooltip="Customers whose receivables have aged past 60 days." />
        <RealCmoKpi id="customer_count" label="Current Customer Count" icon={Users}
          value={params.current_customer_count ?? null} format="number"
          status={params.current_customer_count ? "green" : "blue"}
          sub={params.target_customer_count_12m
            ? `target ${params.target_customer_count_12m} in 12m`
            : "Tap pencil to set"}
          tooltip="Total paying customers right now. Edit inline."
          editable={{
            customerId,
            paramKey: "current_customer_count",
            rawValue: params.current_customer_count,
            transformIn:  (v) => v ?? "",
            transformOut: (v) => v === "" ? null : Math.round(Number(v)),
            lastUpdatedIso: updatedAt.current_customer_count,
            onSaved: handleSaved,
            placeholder: "42",
            formatted: params.current_customer_count != null ? String(params.current_customer_count) : CMO_DASH,
          }} />
      </section>

      {/* Section 2 — AI insights */}
      <InsightsGrid dataAvailable={dataAvailable} />

      {/* Section 3 — Customer Portfolio (real debtors, sorted by outstanding) */}
      <RealCustomerPortfolio debtors={debtorList} debtorCount={debtorCount} totalOutstanding={totalOutstanding} />

      {/* Section 4 — Customer health summary tiles */}
      <Section testid="cmo-health" title="Customer health summary" subtitle="Derived from your live debtor data">
        <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4 text-[12px]">
          <StatTile label="Total customers" value={debtorCount} format="number" testid="cmo-stat-total" />
          <StatTile label="Total outstanding" value={fmtCmoINR(totalOutstanding)} format="raw" testid="cmo-stat-outstanding" />
          <StatTile label="Collection efficiency" value={collEff != null ? `${Number(collEff).toFixed(1)}%` : null} format="raw" testid="cmo-stat-coll" />
          <StatTile label="DSO" value={dso != null ? `${dso} days` : null} format="raw" testid="cmo-stat-dso" />
        </div>
      </Section>

      {/* CRM tiles — populated from Zoho when connected, placeholders otherwise */}
      {(() => {
        const zCRM   = customerData?.zoho?.crm || {};
        const zBooks = customerData?.zoho?.books || {};
        const zohoConnected = !!(zCRM.deal_count || zCRM.contact_count);
        const fmtINR = (n) => (n == null || n === 0 ? null : (n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${Math.round(n).toLocaleString("en-IN")}`));
        const won  = (zCRM.pipeline_by_stage || {});
        const wonName  = Object.keys(won).find((s) => /won/i.test(s));
        const winCount = wonName ? won[wonName] : 0;
        const winRate  = zCRM.deal_count ? (winCount / zCRM.deal_count) * 100 : null;
        return (
          <Section
            testid="cmo-crm"
            title="Pipeline, conversion, LTV & channel ROAS"
            subtitle={
              zohoConnected ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  Live from Zoho CRM
                </span>
              ) : "Connect Zoho CRM to enable these views"
            }
          >
            <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-2">
              <PlaceholderTile label="Pipeline value"
                tooltip={zohoConnected ? `Total amount across ${zCRM.deal_count} deals` : "Connect Zoho CRM to see pipeline data"}
                value={fmtINR(zCRM.deal_total_amount)} />
              <PlaceholderTile label="Total deals"
                tooltip={zohoConnected ? "Open + closed deals in CRM" : "Requires CRM integration"}
                value={zCRM.deal_count ? `${zCRM.deal_count}` : null} />
              <PlaceholderTile label="Win rate"
                tooltip={zohoConnected ? `${winCount} won deals out of ${zCRM.deal_count}` : "Requires CRM integration"}
                value={winRate != null ? `${winRate.toFixed(1)}%` : null} />
              <PlaceholderTile label="Total contacts"
                tooltip={zohoConnected ? "From Zoho CRM Contacts" : "Requires CRM"}
                value={zCRM.contact_count ? `${zCRM.contact_count}` : null} />
              <PlaceholderTile label="Invoices sent"
                tooltip={zohoConnected ? `${zBooks.invoice_count} invoices in Zoho Books` : "Requires Zoho Books"}
                value={zBooks.invoice_count ? `${zBooks.invoice_count}` : null} />
              <PlaceholderTile label="Invoice revenue"
                tooltip={zohoConnected ? "Sum of all invoice totals" : "Requires Zoho Books"}
                value={fmtINR(zBooks.invoice_total)} />
            </div>
          </Section>
        );
      })()}

      {/* NPS + referrals (real WhatsApp template, real NPS, real debtor referrals) */}
      <NPSAndReferrals
        customerData={{
          company_name: companyName,
          owner_name:   customerData?.owner_name,
          nps_score:    npsScore,
          debtors_list: debtorList,
        }}
      />

      {/* CMO Chat (real context already wired) */}
      <CMOChat />
    </div>
  );
}

export default function VirtualCMO() {
  const { customerData, dataAvailable, isLoading, companyName, reload } = useCustomerData();
  return (
    <div data-testid="cmo-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">CMO</div>
          <h2 className="text-[20px] font-bold text-[#1E3A5F]">{companyName ? `${companyName} — your growth co-pilot` : "Your growth co-pilot · Q4 FY26"}</h2>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[12px] text-slate-500">
          <Users size={13} className="text-emerald-600" />
          {dataAvailable
            ? `${(customerData?.debtors?.debtor_count ?? 0)} customers tracked`
            : "Connect Tally for live customer data"}
        </div>
      </div>

      <RealCmoBody customerData={customerData} dataAvailable={dataAvailable} companyName={companyName} reload={reload} />
      </div>
    </div>
  );
}
